import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X, ChevronRight, Megaphone, Share2, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { alertsApi } from '../services/api';

/**
 * DefesaCivilBanner — Banner fixo VERMELHO de alta urgência, exibido no topo
 * em qualquer página. Aparece quando há `public_alerts` com
 * `categoria=defesa_civil` ativo para o município atual.
 *
 * Comportamento intencionalmente agressivo:
 * - Não pode ser dispensado permanentemente (apenas minimizado por sessão).
 * - Pulsação para chamar atenção mesmo no modo claro/escuro.
 * - Posicionamento `sticky` no topo, acima do header.
 * - Recarrega a cada 60s para refletir o estado mais recente.
 */
export default function DefesaCivilBanner({ municipioId }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  const loadAlerts = useCallback(async () => {
    try {
      const params = {};
      if (municipioId) params.municipio_id = municipioId;
      const data = await alertsApi.listPublic(params);
      // Filtra apenas categoria defesa_civil
      const dc = (data || []).filter(
        (a) => a.categoria === 'defesa_civil' || a.tipo === 'defesa_civil',
      );
      setAlerts(dc);
    } catch (e) {
      // Não estoura UI se backend offline; só não mostra banner.
      console.error('DefesaCivilBanner:loadAlerts', e);
    }
  }, [municipioId]);

  useEffect(() => {
    loadAlerts();
    const itv = setInterval(loadAlerts, 60_000);
    return () => clearInterval(itv);
  }, [loadAlerts]);

  // Rotaciona entre múltiplos alertas (caso haja >1 ativo).
  useEffect(() => {
    if (alerts.length <= 1) return;
    const itv = setInterval(() => {
      setCurrentIdx((i) => (i + 1) % alerts.length);
    }, 8000);
    return () => clearInterval(itv);
  }, [alerts.length]);

  // Reseta minimização ao receber novo alerta (id diferente).
  useEffect(() => {
    const a = alerts[currentIdx];
    if (!a) return;
    const minimizedId = sessionStorage.getItem('defesaCivilMinimizedId');
    setMinimized(minimizedId === a.id);
  }, [alerts, currentIdx]);

  const handleClick = async (alert) => {
    try {
      await alertsApi.trackPublic(alert.id, 'click');
    } catch (e) {
      // ignore
    }
    if (alert.cta_url) {
      if (alert.cta_url.startsWith('http')) {
        window.open(alert.cta_url, '_blank', 'noopener');
      } else {
        navigate(alert.cta_url);
      }
    }
  };

  const handleJoinChannel = (alert, e) => {
    e.stopPropagation();
    if (!alert?.whatsapp_canal_url) return;
    try {
      alertsApi.trackPublic(alert.id, 'click');
    } catch (_) {}
    window.open(alert.whatsapp_canal_url, '_blank', 'noopener');
  };

  const handleShareWhatsapp = (alert, e) => {
    e.stopPropagation();
    const text = `🚨 *${alert.titulo}*\n\n${alert.mensagem}${
      alert.whatsapp_canal_url
        ? `\n\nCanal oficial: ${alert.whatsapp_canal_url}`
        : ''
    }`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleMinimize = (alert) => {
    sessionStorage.setItem('defesaCivilMinimizedId', alert.id);
    setMinimized(true);
  };

  const handleExpand = (alert) => {
    sessionStorage.removeItem('defesaCivilMinimizedId');
    setMinimized(false);
  };

  if (alerts.length === 0) return null;
  const alert = alerts[currentIdx];

  // Estado minimizado: pílula vermelha pulsante no canto, clique re-expande.
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => handleExpand(alert)}
        data-testid="defesa-civil-banner-minimized"
        className="fixed top-2 right-2 z-[9999] flex items-center gap-2 rounded-full bg-red-700 px-3 py-1.5 text-xs font-bold text-white shadow-lg ring-2 ring-red-300 animate-pulse hover:bg-red-800"
      >
        <AlertTriangle className="h-4 w-4" />
        ALERTA DEFESA CIVIL
      </button>
    );
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="defesa-civil-banner"
      className="sticky top-0 z-[9999] w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white shadow-lg ring-1 ring-red-900/50"
    >
      <div
        className="absolute inset-0 bg-red-500 opacity-30 animate-pulse pointer-events-none"
        aria-hidden="true"
      />
      <div className="container relative mx-auto flex items-center justify-between gap-3 px-3 py-2 sm:px-4 sm:py-3">
        <div className="flex flex-1 min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/40 animate-pulse">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <button
            type="button"
            onClick={() => handleClick(alert)}
            data-testid="defesa-civil-banner-content"
            className="flex flex-1 min-w-0 flex-col text-left hover:opacity-95"
          >
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide opacity-90">
              <Megaphone className="h-3.5 w-3.5" />
              Defesa Civil
              {alerts.length > 1 && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">
                  {currentIdx + 1}/{alerts.length}
                </span>
              )}
            </span>
            <span className="truncate text-sm font-bold sm:text-base">
              {alert.titulo}
            </span>
            <span className="line-clamp-2 text-xs opacity-90 sm:text-sm">
              {alert.mensagem}
            </span>
          </button>
          {alert.cta_label && (
            <button
              type="button"
              onClick={() => handleClick(alert)}
              data-testid="defesa-civil-banner-cta"
              className="hidden sm:inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50"
            >
              {alert.cta_label}
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
          {alert.whatsapp_canal_url && (
            <button
              type="button"
              onClick={(e) => handleJoinChannel(alert, e)}
              data-testid="defesa-civil-banner-wa-channel"
              title="Entrar no canal oficial do WhatsApp"
              className="hidden md:inline-flex items-center gap-1 rounded-full bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Canal WhatsApp
            </button>
          )}
          <button
            type="button"
            onClick={(e) => handleShareWhatsapp(alert, e)}
            data-testid="defesa-civil-banner-wa-share"
            title="Compartilhar este alerta no WhatsApp"
            className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-white/25"
          >
            <Share2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Compartilhar</span>
          </button>
        </div>
        <button
          type="button"
          onClick={() => handleMinimize(alert)}
          aria-label="Minimizar alerta (continuará visível como pílula no topo)"
          data-testid="defesa-civil-banner-minimize"
          className="shrink-0 rounded-full p-1.5 hover:bg-white/20"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
